import React, { useState, useEffect, useCallback } from "react";
import GeneratorForm from "./GeneratorForm";
import ApprovalDashboard from "./ApprovalDashboard";
import GeneratorStats from "./GeneratorStats";
import MessageLibrary from "./MessageLibrary";
import ComplianceScanner from "./ComplianceScanner";
import PhoneSanitizer from "./PhoneSanitizer";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import "./css/SimulationDataGenerator.css";

/**
 * Main container component for the Simulation Data Generator.
 * Contains three tabs: Generate, Approval Dashboard, Statistics.
 * Admin-only feature for generating phishing simulation messages via AI.
 */
const SimulationDataGenerator = () => {
  const [activeTab, setActiveTab] = useState("generate");
  const [pendingCount, setPendingCount] = useState(0);

  const fetchPendingCount = useCallback(async () => {
    try {
      const stats = await SimulationDataGeneratorAPI.getStats();
      if (stats && typeof stats.pendingCount === "number") {
        setPendingCount(stats.pendingCount);
      }
    } catch (err) {
      console.error("Failed to fetch pending count:", err);
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  const handleGenerationComplete = () => {
    fetchPendingCount();
  };

  const handleApprovalChange = () => {
    fetchPendingCount();
  };

  const switchToApprovalDashboard = () => {
    setActiveTab("approval");
  };

  return (
    <div className="sim-generator">
      <div className="sim-generator-header">
        <h2>Generate Simulation Data</h2>
      </div>

      <div className="sim-generator-tabs">
        <button
          className={`sim-generator-tab ${activeTab === "generate" ? "active" : ""}`}
          onClick={() => setActiveTab("generate")}
        >
          Generate
        </button>
        <button
          className={`sim-generator-tab ${activeTab === "approval" ? "active" : ""}`}
          onClick={() => setActiveTab("approval")}
        >
          Approval Dashboard
          {pendingCount > 0 && (
            <span className="tab-badge">{pendingCount}</span>
          )}
        </button>
        <button
          className={`sim-generator-tab ${activeTab === "stats" ? "active" : ""}`}
          onClick={() => setActiveTab("stats")}
        >
          Statistics
        </button>
        <button
          className={`sim-generator-tab ${activeTab === "library" ? "active" : ""}`}
          onClick={() => setActiveTab("library")}
        >
          Message Library
        </button>
        <button
          className={`sim-generator-tab ${activeTab === "compliance" ? "active" : ""}`}
          onClick={() => setActiveTab("compliance")}
        >
          Compliance
        </button>
        <button
          className={`sim-generator-tab ${activeTab === "phone-sanitizer" ? "active" : ""}`}
          onClick={() => setActiveTab("phone-sanitizer")}
        >
          Phone Sanitizer
        </button>
      </div>

      {activeTab === "generate" && (
        <GeneratorForm
          onGenerationComplete={handleGenerationComplete}
          onSwitchToApproval={switchToApprovalDashboard}
        />
      )}

      {activeTab === "approval" && (
        <ApprovalDashboard onApprovalChange={handleApprovalChange} />
      )}

      {activeTab === "stats" && (
        <GeneratorStats onSwitchToApproval={switchToApprovalDashboard} />
      )}

      {activeTab === "library" && <MessageLibrary />}

      {activeTab === "compliance" && <ComplianceScanner />}

      {activeTab === "phone-sanitizer" && <PhoneSanitizer />}
    </div>
  );
};

export default SimulationDataGenerator;
